import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { PLAYBACK_EVENT, beginPlaybackDeletion, hydratePlayback, localPlayedTracks, mergePlayedTracks } from '../utils/episodePlayback';

import { connect } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react'


import TrackInfoRowsLoading from './TrackInfoRowsLoading';
import BlurPillButton from './BlurPillButton';

import { validTokenExists } from '../firebaseHelper';

import TrackInfoRow from './TrackInfoRow';
import { Modal } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';


export const RecentlyPlayed = (props) => {
    const { playTrack, onSelectionModeChange, uid } = props;

    const [trackInfos, setTrackInfos] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const footer = useRef(null);
    const requestVersion = useRef(0);
    const pending = useRef(false);
    const mounted = useRef(false);
    const currentAccount = useRef(uid);
    currentAccount.current = uid;
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedTrackIds, setSelectedTrackIds] = useState(new Set());
    const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
    const [deleteSelectedInProgress, setDeleteSelectedInProgress] = useState(false);

    const selectedCount = selectedTrackIds.size;

    const episodeCountText = (count) => count === 1 ? '1 episode' : `${count} episodes`;

    const loadPage = useCallback(async (cursor = null) => {
        if (!mounted.current || pending.current) return;
        const version = ++requestVersion.current;
        const account = currentAccount.current;
        const isCurrent = () => mounted.current && version === requestVersion.current && account === currentAccount.current;
        pending.current = true;
        setLoading(true);
        setLoadError(false);
        try {
            await validTokenExists();
            if (!isCurrent()) return;
            const { data } = await axios.post('/recently_played', cursor ? { lastUpdated: cursor } : {});
            if (!isCurrent()) return;
            if (!Array.isArray(data?.trackInfos)) throw new Error('Invalid played episodes response');
            hydratePlayback(account, Object.fromEntries(data.trackInfos.map(track => [track.id, track])));
            setTrackInfos(previous => mergePlayedTracks(account,
                cursor ? previous || [] : [], localPlayedTracks(account), data.trackInfos));
            const nextCursor = data.lastUpdated || null;
            setLastUpdated(nextCursor);
            setHasMore(Boolean(nextCursor) && nextCursor !== cursor);
        } catch {
            if (isCurrent()) {
                setTrackInfos(previous => mergePlayedTracks(account, previous || [], localPlayedTracks(account)));
                setLoadError(true);
            }
        } finally {
            if (isCurrent()) { pending.current = false; setLoading(false); }
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        pending.current = false;
        requestVersion.current += 1;
        setTrackInfos(null);
        setLastUpdated(null);
        setHasMore(false);
        setSelectionMode(false);
        setSelectedTrackIds(new Set());
        setShowDeleteSelectedConfirm(false);
        setDeleteSelectedInProgress(false);
        loadPage();
        const refresh = () => setTrackInfos(previous => mergePlayedTracks(uid, previous || [], localPlayedTracks(uid)));
        window.addEventListener(PLAYBACK_EVENT, refresh);
        return () => { mounted.current = false; requestVersion.current += 1; window.removeEventListener(PLAYBACK_EVENT, refresh); };
    }, [uid, loadPage]);

    useEffect(() => {
        if (!footer.current || !hasMore || loading || loadError || showDeleteSelectedConfirm) return;
        let active = true;
        const observer = new IntersectionObserver(entries => {
            if (!active || !entries.some(entry => entry.isIntersecting)) return;
            active = false;
            observer.disconnect();
            loadPage(lastUpdated);
        }, { rootMargin: '20px' });
        observer.observe(footer.current);
        return () => { active = false; observer.disconnect(); };
    }, [hasMore, loading, loadError, showDeleteSelectedConfirm, lastUpdated, loadPage]);

    useEffect(() => {
        onSelectionModeChange(selectionMode);
    }, [onSelectionModeChange, selectionMode]);

    useEffect(() => {
        return () => {
            onSelectionModeChange(false);
        };
    }, [onSelectionModeChange]);

    const toggleSelectionMode = () => {
        setSelectionMode((currentSelectionMode) => {
            if (currentSelectionMode) {
                setSelectedTrackIds(new Set());
            }
            return !currentSelectionMode;
        });
    };

    const toggleTrackSelection = (trackId) => () => {
        setSelectedTrackIds((currentSelectedTrackIds) => {
            const nextSelectedTrackIds = new Set(currentSelectedTrackIds);
            if (nextSelectedTrackIds.has(trackId)) {
                nextSelectedTrackIds.delete(trackId);
            } else {
                nextSelectedTrackIds.add(trackId);
            }
            return nextSelectedTrackIds;
        });
    };

    const confirmDeleteSelectedTracks = () => {
        if (selectedTrackIds.size === 0) {
            return;
        }
        setShowDeleteSelectedConfirm(true);
    };

    const handleDeleteSelectedTracks = () => {
        const trackIdsToDelete = Array.from(selectedTrackIds);
        if (trackIdsToDelete.length === 0 || deleteSelectedInProgress) {
            return;
        }

        setDeleteSelectedInProgress(true);
        const account = uid;
        const isCurrent = () => mounted.current && account === currentAccount.current;
        let finishDeletion;
        beginPlaybackDeletion(account, trackIdsToDelete)
            .then(finish => { finishDeletion = finish; return validTokenExists(); })
            .then(() => {
                if (!isCurrent()) return null;
                const token = localStorage.getItem('FirebaseIdToken');
                if (jwtDecode(token?.replace(/^Bearer /, '') || '').sub !== account) throw new Error('Account changed');
                return axios.post('/delete_played', { trackIds: trackIdsToDelete }, { headers: { Authorization: token } });
            })
            .then((res) => {
                if (res?.data?.success) { finishDeletion?.(true); finishDeletion = null; }
                if (!isCurrent()) return;
                if (!res.data || !res.data.success) {
                    window.alert('Unexpected error deleting played episodes.');
                    return;
                }

                requestVersion.current += 1;
                pending.current = false;
                setLoading(false);
                setTrackInfos((currentTrackInfos) => (
                    (currentTrackInfos || []).filter((trackInfo) => !trackIdsToDelete.includes(trackInfo.id))
                ));
                setSelectedTrackIds(new Set());
                setSelectionMode(false);
                setShowDeleteSelectedConfirm(false);
            })
            .catch((err) => {
                if (isCurrent()) window.alert('Unexpected error deleting played episodes.');
            })
            .finally(() => {
                finishDeletion?.(false);
                if (isCurrent()) setDeleteSelectedInProgress(false);
            });
    };

    return (
        <div>
            <Modal
                open={showDeleteSelectedConfirm}
                onClose={() => setShowDeleteSelectedConfirm(false)}
            >
                <div className='center'>
                    <div className='savedDeleteSelectedDialog'>
                        <div className='heading'>
                            {deleteSelectedInProgress ? 'Deleting...' : 'Delete played episodes?'}
                        </div>
                        <div className={deleteSelectedInProgress ? 'animate__animated animate__pulse animate__infinite savedDeleteSelectedMessage' : 'savedDeleteSelectedMessage'}>
                            This will remove {episodeCountText(selectedCount)} from Played.
                        </div>
                        {!deleteSelectedInProgress &&
                            <div className='savedDeleteSelectedActions'>
                                <button className='savedDeleteSelectedCancelButton' onClick={() => setShowDeleteSelectedConfirm(false)}>
                                    Cancel
                                </button>
                                <button className='savedDeleteSelectedDeleteButton' onClick={handleDeleteSelectedTracks}>
                                    Delete
                                </button>
                            </div>
                        }
                    </div>
                </div>
            </Modal>

            {trackInfos === null ? (
                <div>
                    <div className='savedTracksHeader'>
                        <div className='largeHeading'>Played</div>
                    </div>
                    <TrackInfoRowsLoading />
                </div>
            ) :
                <div>
                    <div className='savedTracksHeader'>
                        <div className='largeHeading'>
                            Played
                        </div>
                        <div className='savedTracksHeaderActions'>
                            {selectionMode ? (
                                <BlurPillButton
                                    className='pageHeaderActionButton'
                                    onClick={toggleSelectionMode}
                                    disabled={deleteSelectedInProgress}
                                    label='Cancel'
                                />
                            ) : (
                                trackInfos.length > 0 && (
                                    <BlurPillButton
                                        className='pageHeaderActionButton'
                                        onClick={toggleSelectionMode}
                                        label='Select'
                                    />
                                )
                            )}
                        </div>
                    </div>

                    {trackInfos.length !== 0 || hasMore || loadError || loading ? <></> :
                        <div style={{ padding: '20px', fontSize: '16px' }}>
                            Recently played episodes will appear here.
                        </div>
                    }

                    <div className='trackInfoRowsVerticalList'>
                        {trackInfos.map((trackInfo, index) => (
                            <div key={trackInfo.id || index}>
                                <TrackInfoRow
                                    trackInfo={trackInfo}
                                    playTrack={playTrack}
                                    selectionMode={selectionMode}
                                    isSelected={selectedTrackIds.has(trackInfo.id)}
                                    onSelectionToggle={toggleTrackSelection(trackInfo.id)}
                                />
                            </div>
                        ))}
                    </div>

                    {(hasMore || loadError) && <div ref={footer} style={{ textAlign: 'center', minHeight: '44px', padding: '12px 20px' }}>
                        {loadError ? <div role='alert'>
                            <div>Couldn't load played episodes.</div>
                            <button type='button' onClick={() => loadPage(lastUpdated)}>Try again</button>
                        </div> : loading && <div role='status'>Loading more...</div>}
                    </div>}

                    {selectionMode && (
                        <>
                            <div className='savedSelectionBottomSpacer' />
                            <div className='savedSelectionBottomBar'>
                                <div className='savedSelectionCount'>
                                    Selected {selectedCount}
                                </div>
                                <button
                                    className='savedSelectionDeleteButton'
                                    onClick={confirmDeleteSelectedTracks}
                                    disabled={selectedCount === 0 || deleteSelectedInProgress}
                                >
                                    <DeleteOutline style={{ fontSize: '19px', verticalAlign: 'middle' }} />
                                    <span>Delete</span>
                                </button>
                            </div>
                        </>
                    )}
                </div>
            }
        </div>
    );
}

RecentlyPlayed.propTypes = {

}

RecentlyPlayed.defaultProps = {
    onSelectionModeChange: () => {},
}

const mapStateToProps = (state) => ({
    uid: state.user.uid,
})

const mapActionsToProps = {

}

export default connect(mapStateToProps, mapActionsToProps)(RecentlyPlayed);
